import React, { useEffect, useMemo, useState } from 'react';
import { Image, StyleSheet } from 'react-native';
import get from 'lodash/get';
import type lunr from 'lunr';
import debounce from 'lodash/debounce';
// TODO: BEAC-3746: Find the correct way to handle this
import { useActor } from '@xstate/react';
import { DNABox, DNAButton, DNACheckbox, DNAIcon, DNAText, Iffy, InformationMessage, Rotate } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, DNAPopover } from 'src/components/DNA';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useTenant } from 'src/state/redux/selector/tenant';
import ActiveUser from 'src/state/global/ActiveUser';
import SvgCrmIcon from 'src/assets/icons/CrmIcon';
import * as Types from 'src/state/machines/CRM/crmMachineTypes';
import { useUserTenant } from 'src/state/redux/selector/user';
import { CRMAccount, CRMAddress } from 'src/classes/CRM/CRMIndexedDBTypes';
import { Singleton as IndexDbCrm } from 'src/classes/CRM/CRMIndexedDB';
import { CRMAccountORM, ORMTypes } from 'src/types/orms';
import { errors } from 'src/state/machines/CRM/util';
import { ERRORS_ARRAY } from 'src/state/machines/CRM/crmMachine';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import LocalStorageKeys from 'src/components/Authenticator/constants';
import {
  CRM_AUTOMATIC_LOGIN_ATTEMPTS_UPDATED,
  CRM_AUTOMATIC_LOGIN_PREFERENCE_UPDATED,
  ensureCRMAutomaticLoginPreference,
  getCRMAutomaticLoginPreference,
  hasReachedCRMAutomaticLoginAttemptLimit,
  isCRMAutomaticLoginAvailable,
  setCRMAutomaticLoginPreference,
} from 'src/state/machines/CRM/automaticLogin';

export {
  ERROR_REFRESH_TOKEN_MESSAGES,
  useCRMStatus,
  useRefreshToken,
} from 'src/hooks/useCRMStatus';
export type { CRM_STATUS } from 'src/hooks/useCRMStatus';

const loadLunrSearchTools = async () => {
  // Only load Lunr and document-search helpers for the legacy fallback; PWA attendee search should stay on IndexedDB tokens.
  const [lunrModule, documentSearchModule] = await Promise.all([
    import('lunr'),
    import('src/state/redux/selector/documentSearch/documentSearch'),
  ]);

  return {
    lunr: (lunrModule.default ?? lunrModule) as typeof lunr,
    treatLunrSpecialCharacters: documentSearchModule.treatLunrSpecialCharacters,
  };
};

const styles = StyleSheet.create({
  containerStatus: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  sectionWrapper: {
    borderColor: colors['color-gray-80'],
    borderRadius: 4,
    borderWidth: 1,
    padding: 24,
  },
  containerError: {
    margin: 24,
    padding: 24,
    borderWidth: 1,
    borderColor: colors['color-gray-80'],
  },
  container: {
    borderWidth: 1,
    borderRadius: 4,
    borderColor: colors['color-gray-80'],
    padding: 24,
    width: 300,
  },
  connectWrapper: {
    width: '100%',
  },
  crmIntegrationContent: {
    padding: 32,
  },
  automaticLoginSetting: {
    marginTop: 32,
  },
  automaticLoginFailureBanner: {
    marginBottom: 24,
  },
  avatar: {
    borderRadius: 200,
    width: 64,
    height: 64,
  },
});

type CRMContext = {
  cond: {
    canRefreshData: boolean;
    isConnected: boolean;
    canLogin: boolean;
    canLogout: boolean;
    isErrored: boolean;
    isSyncing: boolean;
    disableSync: boolean;
    isLoggingOut: boolean;
    isLoginUnavailable: boolean;
    isLoggedIn: boolean;
    isLoginAttemptEnabled: boolean;
    loginButtonText: string;
    authErrorLabel: string;
  };
  imageUrl: string | undefined;
  displayName: string | undefined;
  state: Types.CRMState,
  send: Types.CRMService['send']
}

const getFormatedName = (model: unknown, formattedName: string): string => {
  const name = formattedName.replace(/\${(.*?)}/g, (_, v) => {
    const value = get(model, v, '')
    if (!value) return ''
    return value
  })

  // if starts with a space, dot or comma, remove it
  return name.replace(/^[\s.,]+/, '')
}

type CRMAccountSearchFilter = (value: CRMAccount, index: number, array: CRMAccount[]) => boolean;

const MAX_CRM_ACCOUNT_SEARCH_RESULTS = 50;
const CRM_ACCOUNT_SEARCH_BATCH_SIZE = 50;

// create context
const CRMContext = React.createContext<CRMContext>(null!);

export const getAccountById = async (id: string) => {
  const config = await IndexDbCrm.getConfig();

  const account = await IndexDbCrm.getById<CRMAccount>('ACCOUNT', id)
  const accountsORM = toORMAccount([account], config);
  return accountsORM[0]
}

export const toORMAccount = (
  accounts: CRMAccount[],
  config: Awaited<ReturnType<typeof IndexDbCrm.getConfig>>,
): CRMAccountORM[] => {
  if (!config) {
    console.error('CRM config is not defined');
    return [];
  }

  return accounts.reduce<CRMAccountORM[]>((acc, account) => {
    if (!account) {
      return acc;
    }

    return [...acc, {
      model: account,
      type: 'CRM_ACCOUNT' as ORMTypes.CRM_ACCOUNT,
      meta: {
        formattedName: getFormatedName(account, config.displaySettings.shortened),
        // TODO: Format long name is pending to be implemented
      },
      relations: {
        addresses: (account?.addresses as CRMAddress[])?.map((address) => (
          {
            model: address,
            type: 'CRM_ADDRESS' as ORMTypes.CRM_ADDRESS,
            meta: {
              formattedName: getFormatedName(address, config.addressSettings.displaySettings.shortened),
              // TODO: Format long name is pending to be implemented
            },
          }
        )),
      },
    }];
  }, [])
}

async function lunrQueryBuilder(search: string): Promise<string> {
  const {
    lunr: lunrModule,
    treatLunrSpecialCharacters,
  } = await loadLunrSearchTools();
  const tokens = lunrModule.tokenizer(search);
  const query = tokens.reduce((acc, token) => {
    const cleanedToken = treatLunrSpecialCharacters(token.toString().trim()).toLowerCase();
    if (cleanedToken.length < 3) {
      return acc;
    }
    return acc + `${cleanedToken}* ${cleanedToken} `;
  }, '');

  return query;
}

async function getSearchAccounts(
  result: lunr.Index.Result[],
  filterFn?: CRMAccountSearchFilter,
): Promise<CRMAccountORM[]> {
  const accounts: CRMAccountORM[] = [];
  const config = await IndexDbCrm.getConfig();

  for (
    let start = 0;
    start < result.length &&
    accounts.length < MAX_CRM_ACCOUNT_SEARCH_RESULTS;
    start += CRM_ACCOUNT_SEARCH_BATCH_SIZE
  ) {
    const batch = result.slice(start, start + CRM_ACCOUNT_SEARCH_BATCH_SIZE);
    let hydratedAccounts = await IndexDbCrm.filterById('ACCOUNT', batch.map((item) => item.ref));

    hydratedAccounts = hydratedAccounts.filter(Boolean);

    if (filterFn) {
      hydratedAccounts = hydratedAccounts.filter(filterFn);
    }

    accounts.push(...toORMAccount(hydratedAccounts, config));
  }

  const indexedAccounts: Record<string, CRMAccountORM> = {};
  accounts.slice(0, MAX_CRM_ACCOUNT_SEARCH_RESULTS).forEach((account) => {
    indexedAccounts[account.model.id] = account;
  });

  const sortedAccounts: CRMAccountORM[] = [];

  for (const account of result) {
    if (indexedAccounts[account.ref]) {
      sortedAccounts.push(indexedAccounts[account.ref]);
    }

    if (sortedAccounts.length >= MAX_CRM_ACCOUNT_SEARCH_RESULTS) {
      break;
    }
  }

  return sortedAccounts;
}

export const useCRMAccountSearch = () => {
  const [accounts, setAccounts] = useState<CRMAccountORM[]>([])

  const resetAccountsArray = () => {
    setAccounts([]);
  };

  const search = useMemo(() => {
    const debouncedSearch = debounce(async (search: string, filterFn?: CRMAccountSearchFilter) => {
      if (IndexDbCrm.isAccountSearchIndexEnabled()) {
        // IndexedDB token search avoids loading the full Lunr account index on PWA and supported tablet paths.
        try {
          const localSearchAccounts = await IndexDbCrm.searchAccountsByTokenPrefix(
            search,
            filterFn,
          );
          const config = await IndexDbCrm.getConfig();
          const ormAccounts = toORMAccount(localSearchAccounts, config);
          setAccounts(ormAccounts);
        } catch {
          setAccounts([]);
        }
        return;
      }

      const query = await lunrQueryBuilder(search);
      const result = await IndexDbCrm.searchIndex(query);

      if (!result?.length) {
        return setAccounts([]);
      }

      const sortedAccounts = await getSearchAccounts(result, filterFn);
      setAccounts(sortedAccounts)
    }, 500);

    return debouncedSearch;
  }, []);

  useEffect(() => () => {
    search.cancel();
  }, [search]);

  return {
    accounts,
    search,
    resetAccountsArray,
    getAccountById: (id: string) => getAccountById(id),
  }
}

export const CRMIntegrationProvider = ({ children }) => {
  const { crmMachine, isOnline } = useAppSettings()
  const [state, send] = useActor(crmMachine);
  const imageUrl = state.context.crmSession?.authInformation?.userInfo?.thumbnail
  const displayName = state.context.crmSession?.authInformation?.userInfo?.displayName
  const isRefreshingDataTag = state.hasTag('REFRESH_DATA')
  const isRefreshingTokenTag = state.hasTag('REFRESH_TOKEN')
  const isSyncing = state.context.crmSyncStatus === Types.CRMSyncStatus.SYNCING ||
    state.context.crmSyncStatus === Types.CRMSyncStatus.RE_SYNCING
  const isCRMConfigEnabled = state.context.crmAvailabilityStatus === Types.CRMAvailabilityStatus.ENABLED;
  const canLogin = state.can('LOGIN_CRM') && !isRefreshingTokenTag && !isSyncing && !isRefreshingDataTag;
  const disableSync = state.context.isAuthenticating || isRefreshingTokenTag;
  const isLoginUnavailable = !isCRMConfigEnabled || !isOnline;
  const hasAuthToken = !!(state.context.crmSession?.accessToken || state.context.crmSession?.encryptedSessionId);
  const isLoggedIn = hasAuthToken && !state.matches('showError');
  const isErrored = state.matches('showError');
  const isPopUpError = state.context?.error === errors.not_allowed_pop_up;
  const reaAuthMessageToShow = ERRORS_ARRAY.includes(state.context?.error || '') ? 'Re-Authenticate' : 'Reconnect';
  const loginButtonText = isErrored && !isPopUpError ? reaAuthMessageToShow : 'Connect to CRM';
  const authErrorLabel = Object.values(errors).find(e => e === state.context.error) ||
    'CRM is disconnected, please reconnect. If the issue persists, please contact support.';

  const cond = {
    canRefreshData: state.can({ type: 'REFRESH_DATA', payload: { trigger: 'USER' } }),
    isConnected: hasAuthToken,
    canLogin,
    canLogout: state.can('LOGOUT_CRM') && !isRefreshingTokenTag && !isSyncing && !isRefreshingDataTag,
    isErrored,
    isSyncing: isSyncing || isRefreshingTokenTag || isRefreshingDataTag,
    isLoggingOut: state.context.connectionStatus === Types.CRMConnectionStatus.DISCONNECTING,
    // TODO: BEAC-3746: Consider to move to a tag
    disableSync,
    isLoginUnavailable,
    isLoggedIn,
    isLoginAttemptEnabled: !isLoginUnavailable && !isLoggedIn && !disableSync && canLogin,
    loginButtonText,
    authErrorLabel,
  }

  const value = {
    cond,
    imageUrl,
    displayName,
    state,
    send,
  }

  return (
    <CRMContext.Provider value={value}>
      {children}
    </CRMContext.Provider>
  )
}

export const useCrmStateMachine = () => {
  const context = React.useContext(CRMContext);

  if (context === undefined) {
    throw new Error('useCrmStateMachine must be used within a CRMIntegrationProvider')
  }

  return context;
}

const CRMAvatar = () => {
  const { imageUrl } = useCrmStateMachine();
  const [image, setImage] = React.useState(imageUrl);
  return (
    <DNABox alignX="center">
      {image
        ? <Image
            source={{ uri: imageUrl }}
            style={styles.avatar}
            onError={() => setImage(undefined)}
        />
        // TODO BEAC-3746: try to us a DNAIcon/DNAButton instead
        : < SvgCrmIcon />
      }
    </DNABox>
  )
}

const CRMConnect = () => {
  const currentTenant = useUserTenant();
  const { isOnline } = useAppSettings();
  const { cond, send, state } = useCrmStateMachine()
  const { isConnected, canLogin, canLogout, isLoggingOut } = cond
  const { isErrored } = cond;
  const isPopUpError = state.context?.error === errors.not_allowed_pop_up
  const reaAuthMessageToShow = ERRORS_ARRAY.includes(state.context?.error || '') ? 'Re-Authenticate' : 'Reconnect'
  const ConnectionText = isErrored && !isPopUpError ? reaAuthMessageToShow : isConnected ? 'Log out' : 'Connect';

  const handleDisconnect = () => {
    analytics.track('CRM_DISCONNECT', {
      category: 'CRM',
      integration: state.context.crmConfig?.crmIntegrationType,
    })

    send({
      type: 'LOGOUT_CRM',
      data: {
        reason: Types.LOGOUT_DUE_TO.USER_LOGOUT,
      },
    })
  }

  const handleConnect = async () => {
    send({
      type: 'LOGIN_CRM',
      payload: currentTenant?.config.crmIntegration,
    })
  }

  if (!isOnline) return null

  return (
    <DNAButton
      disabled={isLoggingOut || !canLogin || cond.disableSync || (!canLogout && ConnectionText === 'Log out')}
      appearance="outline"
      status="tertiary"
      stretch
      onPress={isErrored
        ? handleConnect
        : isConnected
          ? handleDisconnect
          : handleConnect
      }
    >
      <DNABox fill>
        <Iffy is={cond.disableSync || cond.isLoggingOut}>
          <Rotate>
            <DNAIcon.Styled name="arrow-clockwise" status="tertiary" disabled />
          </Rotate>
        </Iffy>
        <DNAText
          status={
            !(canLogin || canLogout) ||
              cond.disableSync ||
              cond.isLoggingOut
              ? 'subtle'
              : undefined
          }
        >
          {ConnectionText}
        </DNAText>
      </DNABox>
    </DNAButton>
  )
}

function useDelayedState<T>(value: T, delay: number): T {
  const [delayedValue, setDelayedValue] = useState<T>(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDelayedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeout);
    };
  }, [value, delay]);

  return delayedValue;
}

const SyncingChip = () => {
  const { cond, state } = useCrmStateMachine()

  const ChipText = cond.isConnected ? 'CONNECTED' : '';

  const isSyncing = cond.isSyncing && !cond.isLoggingOut && !state.context.isAuthenticating
  const isConnected = cond.isConnected && !cond.isSyncing

  const showSyncing = useDelayedState(isSyncing, 1000);
  const showConnected = useDelayedState(isConnected, 1000);

  return (
    <>
      {showSyncing &&
        <DNABox alignY="center" spacing="sm">
          <DNAPopover >
            <DNAPopover.Anchor>
              <Rotate>
                <DNAIcon.Styled name="arrow-clockwise" size="xl" />
              </Rotate>
            </DNAPopover.Anchor>
            <DNAPopover.Content>
              <DNAText colorMode="dark">Syncing...</DNAText>
            </DNAPopover.Content>
          </DNAPopover>
          <DNAChip.Beacon status="primary" children="SYNCING..." />
        </DNABox>
      }
      {showConnected && !cond.isErrored &&
        <DNAChip.Beacon status="success" children={ChipText} />
      }
    </>
  )
}

const CRMAutomaticLoginSetting = () => {
  const currentTenant = useUserTenant();
  const isAutomaticLoginFlagEnabled = useFeatureFlags('BEAC_7921_crm_automatic_login');
  const isAutomaticLoginAvailable = isCRMAutomaticLoginAvailable({
    crmIntegration: currentTenant?.config.crmIntegration,
    isFeatureFlagEnabled: isAutomaticLoginFlagEnabled,
  });
  const [isChecked, setIsChecked] = useState(getCRMAutomaticLoginPreference);

  useEffect(() => {
    if (!isAutomaticLoginAvailable) {
      return;
    }

    setIsChecked(ensureCRMAutomaticLoginPreference());
  }, [isAutomaticLoginAvailable]);

  useEffect(() => {
    const handlePreferenceUpdate = (e?: StorageEvent | Event) => {
      if (
        e instanceof StorageEvent &&
        e.key &&
        e.key !== LocalStorageKeys.CRM_AUTOMATIC_LOGIN_PREFERENCE
      ) {
        return;
      }
      setIsChecked(getCRMAutomaticLoginPreference());
    };

    window.addEventListener('storage', handlePreferenceUpdate);
    window.addEventListener(CRM_AUTOMATIC_LOGIN_PREFERENCE_UPDATED, handlePreferenceUpdate);

    return () => {
      window.removeEventListener('storage', handlePreferenceUpdate);
      window.removeEventListener(CRM_AUTOMATIC_LOGIN_PREFERENCE_UPDATED, handlePreferenceUpdate);
    };
  }, []);

  if (!isAutomaticLoginAvailable) {
    return null;
  }

  const onToggleAutomaticLogin = () => {
    const nextValue = !isChecked;
    setIsChecked(nextValue);
    setCRMAutomaticLoginPreference(nextValue);
  }

  return (
    <DNABox alignY="center" spacing="xs" style={styles.automaticLoginSetting}>
      <DNACheckbox
        checked={isChecked}
        onChange={onToggleAutomaticLogin}
      />
      <DNAText>
        Automatic login
      </DNAText>
      <DNAPopover>
        <DNAPopover.Anchor>
          <DNAIcon.Styled name="question" size="sm" status="subtle" />
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText colorMode="dark">
            Allow Beacon to attempt CRM login automatically on this device when needed.
          </DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    </DNABox>
  )
}

const CRMAutomaticLoginFailureBanner = () => {
  const currentTenant = useUserTenant();
  const isAutomaticLoginFlagEnabled = useFeatureFlags('BEAC_7921_crm_automatic_login');
  const isAutomaticLoginAvailable = isCRMAutomaticLoginAvailable({
    crmIntegration: currentTenant?.config.crmIntegration,
    isFeatureFlagEnabled: isAutomaticLoginFlagEnabled,
  });
  const [hasReachedAttemptLimit, setHasReachedAttemptLimit] = useState(
    hasReachedCRMAutomaticLoginAttemptLimit,
  );

  useEffect(() => {
    const handleAttemptsUpdate = () => {
      setHasReachedAttemptLimit(hasReachedCRMAutomaticLoginAttemptLimit());
    };

    window.addEventListener(CRM_AUTOMATIC_LOGIN_ATTEMPTS_UPDATED, handleAttemptsUpdate);

    return () => {
      window.removeEventListener(CRM_AUTOMATIC_LOGIN_ATTEMPTS_UPDATED, handleAttemptsUpdate);
    };
  }, []);

  if (!isAutomaticLoginAvailable || !hasReachedAttemptLimit) {
    return null;
  }

  return (
    <DNABox appearance="col" style={styles.automaticLoginFailureBanner}>
      <InformationMessage
        text="Automatic login failed. Please contact support."
        variance="warning"
      />
    </DNABox>
  )
}

const CRMIntegration = () => {
  const { cond, displayName } = useCrmStateMachine()
  const { isConnected } = cond
  const tenant = useTenant(ActiveUser.user!.tenantId);

  if (!tenant) {
    return null;
  }

  // This case shouldn't happen, but just in case due the tab is not visible for tenants that don't have CRM Integration
  if (!tenant.tenant.config.crmIntegration) {
    return <DNAText status="warning">CRM Integration is not enabled for this tenant</DNAText>
  }

  return (
    <DNABox appearance="col" style={styles.crmIntegrationContent}>
      <CRMAutomaticLoginFailureBanner />
      <DNABox
        appearance="col"
        alignY="center"
        alignX="center"
        spacing="md"
        style={styles.container}
        childStyle={[3, styles.connectWrapper]}
      >
        <DNABox spacing="between" alignX="center" alignY="center">
          <DNABox alignY="center" spacing="sm">
            <DNAText>{tenant.tenant.config.crmIntegration.name || ''}</DNAText>
          </DNABox>
        </DNABox>
        <SyncingChip />
        {isConnected &&
          <DNABox appearance="col" spacing="md">
            <CRMAvatar />
            <DNABox alignX="center">
              <DNAText status="subtle">
                {displayName}
              </DNAText>
            </DNABox>
          </DNABox>
        }
        { /* Connect Container */}
        <CRMConnect />
      </DNABox>
      <CRMAutomaticLoginSetting />
    </DNABox>
  )
}

export const CRMStatus = () => {
  const { isOnline } = useAppSettings();
  const { cond, displayName, send, state } = useCrmStateMachine()
  const { isSyncing, isConnected, isErrored } = cond
  const tenant = useTenant(ActiveUser.user!.tenantId);
  const config = tenant?.tenant.config.crmIntegration

  const ChipText = isConnected && !isSyncing ? 'CONNECTED' : '';
  const lastSyncTime = state.context.lastSyncedAt

  return (
    <DNABox style={{ width: 400, backgroundColor: colors['color-text-white'] }} appearance="col">
      <DNABox style={styles.containerStatus} fill appearance="col" spacing="md">
        <DNABox style={styles.sectionWrapper} appearance="col" spacing="lg">
          { /* Connecting Status */}
          <DNABox spacing="between" alignX="center" alignY="center">
            <DNABox alignY="center" spacing="sm">
              <Iffy is={isSyncing}>
                <Rotate>
                  <DNAIcon.Styled name="arrow-clockwise" size="xl" />
                </Rotate>
              </Iffy>
              <DNAText bold h5>
                {tenant?.tenant.config.crmIntegration?.name || ''}
              </DNAText>
            </DNABox>
            <Iffy is={isConnected && !isErrored && !isSyncing}>
              <DNAChip.Beacon status="success" children={ChipText} />
            </Iffy>
          </DNABox>

          { /* Avatar */}
          <Iffy is={isConnected || displayName}>
            <DNABox alignY="center" spacing="md">
              <CRMAvatar />
              <DNAText>
                {displayName}
              </DNAText>
            </DNABox>
          </Iffy>

          { /* Last Sync */}
          <DNABox alignY="center">
            {lastSyncTime && isConnected &&
              <DNAText status="subtle">Last Synced on {
                lastSyncTime.toLocaleDateString(undefined,
                  { day: 'numeric', month: 'short', year: 'numeric' }) + ' at ' +
                lastSyncTime.toLocaleTimeString(undefined,
                  { hour: '2-digit', minute: '2-digit' })
              }</DNAText>}
          </DNABox>

          { /* Sync Button */}
          <Iffy is={!isErrored && isOnline}>
            <DNAButton
              appearance="outline"
              status="tertiary"
              iconLeft={isSyncing ? undefined : 'arrow-clockwise'}
              stretch
              onPress={() => send({ type: 'REFRESH_DATA', payload: { trigger: 'USER' } })}
              disabled={!isOnline || cond.disableSync || isSyncing || !config || cond.isLoggingOut}
            >
              <DNABox fill>
                <Iffy is={isSyncing}>
                  <Rotate>
                    <DNAIcon.Styled name="arrow-clockwise" status="tertiary" disabled />
                  </Rotate>
                </Iffy>
                <DNAText status={isSyncing ? 'subtle' : undefined}>
                  Sync latest from CRM
                </DNAText>
              </DNABox>
            </DNAButton>
          </Iffy>
          <Iffy is={isErrored}>
            <CRMConnect />
          </Iffy>
        </DNABox>
      </DNABox>

      { /* Error */}
      {isErrored && <DNABox style={{ marginVertical: 12, marginHorizontal: 12 }} fill alignX="center">
        <CRMError />
      </DNABox>}
    </DNABox>)
}

export const CRMError = () => {
  const { cond, state } = useCrmStateMachine()
  const { isErrored } = cond

  useEffect(() => {
    if (state.context.error) {
      console.error('CRM Error', state.context.error)
    }
  }, [state.context.error])

  if (!isErrored) return null

  const error = Object.values(errors).find(e => e === state.context.error) ||
    'CRM is disconnected, please reconnect. If the issue persists, please contact support.'

  return (
    <DNAText status="warning" style={{ textAlign: 'center' }}>
      {error}
    </DNAText>
  )
}

export const CRMReauth = () => {
  const { cond } = useCrmStateMachine()
  const { isErrored } = cond

  return (
    <DNABox style={{ width: 300, backgroundColor: 'white', paddingTop: 24 }} appearance="col" >
      <CRMConnect />
      <Iffy is={isErrored}>
        <DNABox alignX="center" alignY="center" fill>
          <DNABox style={{ marginTop: 4, marginBottom: 12 }}>
            <CRMError />
          </DNABox>
        </DNABox>
      </Iffy>
    </DNABox>
  )
}

export default () => {
  return (
    <CRMIntegrationProvider>
      <CRMIntegration />
      <DNABox style={{ marginVertical: 4 }}>
        <CRMError />
      </DNABox>
    </CRMIntegrationProvider>
  );
}
